import React, { useMemo } from 'react';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import useModal from 'src/hooks/useModal';
import WritesPausedBanner from '../FeatureUnavailable/WritesPausedBanner';

import OutputsPicker from './OutputsPicker';
import ReviewSummary from './ReviewSummary';
import StepIndicator from './StepIndicator';
import { countKinds, MAX_REPROCESS_ASSETS, selectionCount } from './reprocessOptions';
import { useReprocessFlow } from './useReprocessFlow';
import styles from './ReprocessFlow.module.scss';

type ReprocessModalProps = BaseModalProps & {
  assetIds: string[];
  /** Content type per selected id, from the page's search payload. */
  contentTypes: (string | undefined)[];
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Reprocess the current selection (ui#1018): assets → outputs → review.
 * The admin page (`/reprocess-assets`) runs the same flow from pasted ids.
 */
export const ReprocessModal: React.FC<ReprocessModalProps> = ({ assetIds, contentTypes, ...modalProps }) => {
  const { closeModal } = useModal();
  // A selection can include assets the page has not loaded (select-all on a
  // collection); then the types come from the estimate instead.
  const allKnown = contentTypes.length === assetIds.length && contentTypes.every(Boolean);
  const known = useMemo(() => (allKnown ? countKinds(contentTypes) : undefined), [allKnown, contentTypes]);
  const flow = useReprocessFlow(assetIds, known);
  const counts = flow.counts;
  const other = counts ? assetIds.length - counts.image - counts.video : 0;
  const overLimit = assetIds.length > MAX_REPROCESS_ASSETS;

  const handleSubmit = async () => {
    if (await flow.submit()) closeModal();
  };

  return (
    <BaseModal {...modalProps}>
      <div className={styles.modalBody} data-test="reprocess-modal">
        <h3 className={styles.title}>Reprocess assets</h3>
        <StepIndicator current={flow.step} />
        {flow.writesPaused && <WritesPausedBanner reason={flow.pausedReason} />}

        {flow.step === 'assets' && (
          <>
            <p className={styles.lede}>
              {plural(assetIds.length, 'asset')} selected
              {counts
                ? `: ${plural(counts.image, 'image')}, ${plural(counts.video, 'video')}${
                    other > 0 ? `, ${plural(other, 'other file')}` : ''
                  }.`
                : '.'}
            </p>
            {!counts && flow.probe.isLoading && <p className={styles.hint}>Checking the selection…</p>}
            {!counts && flow.probe.error && (
              <p className={styles.error} role="alert">
                {flow.probe.error.status === 403
                  ? 'You do not have permission to reprocess these assets.'
                  : flow.probe.error.message || 'Could not read the selection.'}
              </p>
            )}
            {overLimit && (
              <p className={styles.error} role="alert">
                Up to {MAX_REPROCESS_ASSETS} assets can be reprocessed at once. Narrow the selection.
              </p>
            )}
            <p className={styles.hint}>
              Reprocessing re-runs outputs on files you already hold, for example after changing a
              conversion preset. Originals are not changed.
            </p>
          </>
        )}

        {flow.step === 'outputs' && (
          <OutputsPicker groups={flow.groups} value={flow.selection} onChange={flow.setSelection} />
        )}

        {flow.step === 'review' && (
          <ReviewSummary
            groups={flow.groups}
            selection={flow.selection}
            estimate={flow.estimate.data}
            loading={flow.estimate.isLoading}
            error={flow.estimate.error}
          />
        )}

        <div className={styles.actions}>
          {flow.step === 'assets' ? (
            <Button mode="flat" onClick={closeModal}>
              Cancel
            </Button>
          ) : (
            <Button
              mode="flat"
              onClick={() => flow.setStep(flow.step === 'review' ? 'outputs' : 'assets')}
              disabled={flow.submitting}
            >
              Back
            </Button>
          )}
          {flow.step === 'assets' && (
            <Button mode="primary" onClick={() => flow.setStep('outputs')} disabled={overLimit || !assetIds.length || !counts}>
              Next
            </Button>
          )}
          {flow.step === 'outputs' && (
            <Button
              mode="primary"
              onClick={() => flow.setStep('review')}
              disabled={selectionCount(flow.selection) === 0}
              data-test="reprocess-next-review"
            >
              Review
            </Button>
          )}
          {flow.step === 'review' && (
            <Button
              mode="primary"
              onClick={handleSubmit}
              loading={flow.submitting}
              disabled={!flow.canSubmit || flow.estimate.isLoading}
              data-test="reprocess-submit"
            >
              Reprocess {plural(flow.submitIds.length, 'asset')}
            </Button>
          )}
        </div>
      </div>
    </BaseModal>
  );
};

export default ReprocessModal;
